"use client";
import React, { createContext, useContext, useState } from 'react';

const BlogContext = createContext();
export const useBlog = () => useContext(BlogContext);

const blogPosts = [
  {
    id: 1,
    title: "Custom Photo Albums: Turning Memories Into Timeless Keepsakes",
    slug: "custom-photo-albums-timeless-keepsakes",
    excerpt: "A photo album isn't just a collection of pictures — it's a story. Discover how custom photo albums from Manaprints transform your favourite moments into a beautifully crafted heirloom you'll cherish forever.",
    content: `
<p>In a world of digital scrolling and fleeting screen moments, a printed photo album stands apart. It's tactile, permanent, and deeply personal. At Manaprints, our custom photo albums let you tell your story exactly the way you want — from honeymoon highlights to baby's first year, college memories to family reunions.</p>

<h3>Why Print Your Photos?</h3>
<p>Research shows that printed photos are looked at far more often than digital ones buried in phones. A beautifully bound album on your coffee table invites people in, sparks conversations, and keeps memories alive in a way a phone gallery never can.</p>

<h3>Choosing the Right Size & Layout</h3>
<p>Manaprints offers multiple album sizes — from compact 5×7 to grand 12×12. Smaller albums suit intimate collections like anniversary trips, while larger formats are perfect for weddings or milestone events where you want full-bleed, dramatic photography.</p>

<h3>Cover Options That Make an Impression</h3>
<p>The cover is the first thing you see, and it sets the tone for everything inside. Choose from soft-touch matte, glossy hardcover, or linen-wrapped finishes. Add a title, date, or a meaningful quote to make it uniquely yours.</p>

<h3>Inside Pages: Quality That Shows</h3>
<p>Every page in a Manaprints album is printed on premium paper — vibrant enough to pop with colour photography, yet refined enough for black-and-white art prints. The lay-flat binding means no gutter crease interrupting your panoramic shots.</p>

<h3>Perfect Gifting Ideas</h3>
<ul>
<li>Wedding and anniversary albums for couples</li>
<li>Baby's first year — month-by-month growth</li>
<li>Family reunion or vacation photo book</li>
<li>Graduation portfolio for school memories</li>
<li>Corporate event summary for clients</li>
</ul>

<p>Whether you're gifting a photo album or keeping one for yourself, the effort you put into creating it reflects the love behind every page. Start your custom album at Manaprints today and give your memories the home they deserve.</p>
    `,
    author: "Manaprints Team",
    publishedDate: "2026-06-15",
    category: "Photo Albums",
    tags: ["photo album", "memories", "custom printing", "gifts", "personalisation"],
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=450&fit=crop",
    featured: true,
  },
  {
    id: 2,
    title: "Personalised Pillow Covers: Where Comfort Meets Creativity",
    slug: "personalised-pillow-covers-comfort-creativity",
    excerpt: "Pillow covers are one of the easiest — and most affordable — ways to personalise your home. From family portraits to fun quotes, find out how Manaprints makes your living space truly your own.",
    content: `
<p>Your home should feel like you. And nothing brings personality to a sofa, bedroom, or reading nook quite like a custom pillow cover. Manaprints' personalised pillow covers combine high-quality fabric printing with designs that are as unique as the people who own them.</p>

<h3>The Power of a Personal Touch</h3>
<p>Mass-produced home décor fills every furniture store. Custom pillow covers let you break away from the generic and fill your space with something meaningful — a photo of your pet, a family portrait, a favourite song lyric, or a bold graphic that matches your personality exactly.</p>

<h3>Material & Print Quality</h3>
<p>Manaprints uses dye-sublimation printing on premium microfibre and polyester fabric blends. This process pushes colour directly into the fibres rather than sitting on top, making the print vibrant, wash-safe, and long-lasting — no cracking, fading, or peeling over time.</p>

<h3>Design Ideas for Every Room</h3>
<p><strong>Living Room:</strong> Family photos in a clean grid layout, or a minimalist monogram design.</p>
<p><strong>Kids' Room:</strong> Cartoon characters, the child's name, or their artwork printed large.</p>
<p><strong>Bedroom:</strong> Romantic couple portrait, anniversary date, or a meaningful quote.</p>
<p><strong>Office Corner:</strong> Motivational typography or a subtle brand logo.</p>

<h3>Bulk Orders for Events & Corporates</h3>
<p>Planning a wedding? Gift guests personalised pillow covers with your names and wedding date. Running a corporate retreat? Branded pillow covers make memorable, practical giveaways. Manaprints offers competitive pricing for bulk orders with consistent quality across every piece.</p>

<h3>Sizing Options</h3>
<p>We offer standard 12×12, 14×14, 16×16, and 18×18 inch covers with zip closures for easy removal and washing. Cushion inserts are available separately.</p>

<p>Ready to transform your space? Upload your photo or design and see a preview before you order. Custom pillow covers from Manaprints ship across India with careful packaging to ensure your print arrives in perfect condition.</p>
    `,
    author: "Manaprints Team",
    publishedDate: "2026-06-08",
    category: "Pillow Covers",
    tags: ["pillow cover", "home decor", "personalisation", "custom printing", "gifts"],
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=450&fit=crop",
    featured: true,
  },
  {
    id: 3,
    title: "Custom Photo Frames: The Art of Displaying What Matters",
    slug: "custom-photo-frames-art-of-displaying",
    excerpt: "A frame does more than hold a picture — it elevates it. Explore how Manaprints' custom frames can transform a single photograph into a centrepiece that anchors your entire room.",
    content: `
<p>There's a reason people say "it's all in the framing." The right frame turns a good photo into a gallery-worthy display. Manaprints' custom photo frames give you full control over size, material, and design — so your memories are presented the way they deserve to be.</p>

<h3>Why Custom Frames Beat Off-the-Shelf</h3>
<p>Standard frames from stores come in standard sizes — and your photos rarely do. Custom frames are cut to your exact dimensions, eliminating awkward cropping or excess white borders. More importantly, you choose the style, colour, and finish that suits your wall and your aesthetic.</p>

<h3>Frame Styles at Manaprints</h3>
<p><strong>Classic Wood Finish:</strong> Warm, timeless, and versatile. Perfect for family portraits and vintage-style photography.</p>
<p><strong>Sleek Metal:</strong> Modern and minimalist, ideal for contemporary homes and office spaces.</p>
<p><strong>Collage Frames:</strong> Display multiple photos in one frame — great for telling a story across time.</p>
<p><strong>Floating Frames:</strong> Give your photo a gallery-style look with a visible gap between the print and frame edge.</p>

<h3>Popular Occasions for Custom Frames</h3>
<ul>
<li>Wedding and anniversary gifts</li>
<li>Baby shower and birth announcement displays</li>
<li>Graduation ceremony memorials</li>
<li>Corporate recognition awards</li>
<li>Home gallery walls</li>
</ul>

<h3>Getting the Best Print for Your Frame</h3>
<p>At Manaprints, your photo is printed in-house on premium photo paper before being mounted. We ensure colour accuracy and sharpness so the framed piece looks exactly as intended. For best results, we recommend uploading images at a minimum of 300 DPI for the intended print size.</p>

<h3>Gift Wrapping & Delivery</h3>
<p>All framed prints are shipped in custom protective packaging to prevent transit damage. Gift wrapping options are available, making it easy to send a beautifully presented frame directly to the recipient.</p>

<p>From a single portrait to a full gallery wall arrangement, Manaprints' custom frames are designed to make every photo shine. Browse the collection and find the perfect frame for your next print today.</p>
    `,
    author: "Manaprints Team",
    publishedDate: "2026-05-28",
    category: "Frames",
    tags: ["photo frame", "wall decor", "home décor", "custom printing", "gifts"],
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1513519245088-0e12902e5a38?w=800&h=450&fit=crop",
    featured: false,
  },
  {
    id: 4,
    title: "Design Your Own Mobile Case: Protection Meets Personality",
    slug: "design-own-mobile-case-protection-personality",
    excerpt: "Your phone goes everywhere with you — so why settle for a plain case? Manaprints' custom mobile cases let you carry your personality in your pocket, with prints that are as tough as they are beautiful.",
    content: `
<p>Mobile cases have become one of the most popular personalised accessories in India — and for good reason. They're practical, highly visible, and replaced regularly enough to stay fresh. Manaprints offers custom mobile cases for all major phone models with vibrant, durable printing.</p>

<h3>Types of Mobile Cases We Print On</h3>
<p><strong>Slim Hard Cases:</strong> Lightweight and stylish with sharp print quality. Best for those who prefer minimal bulk.</p>
<p><strong>Tough Protective Cases:</strong> Dual-layer protection with full-surface customisation. Ideal for outdoor lifestyles.</p>
<p><strong>Transparent Cases:</strong> Show off your phone's original colour while adding a custom design overlay.</p>

<h3>What Makes a Great Mobile Case Design?</h3>
<p>The best case designs are bold enough to be seen but cohesive enough to not look cluttered. Here are some winning approaches:</p>
<ul>
<li><strong>Photo cases:</strong> Your pet, partner, travel shot, or family photo in full bleed.</li>
<li><strong>Typography cases:</strong> A favourite quote, your name, or motivational words in a striking font.</li>
<li><strong>Abstract art:</strong> Geometric patterns, watercolour textures, or gradient designs that look premium.</li>
<li><strong>Branded cases:</strong> Logo and brand colours for business professionals or content creators.</li>
</ul>

<h3>Print Technology & Durability</h3>
<p>Manaprints uses UV-resistant inks with a protective coating that prevents the print from scratching or fading under regular use. The design wraps around the edges for a seamless look, not a sticker feel.</p>

<h3>Popular Gifting Ideas</h3>
<p>Custom mobile cases make perfect birthday, festival, or graduation gifts. Pair it with a photo album or custom frame for a complete personalised gift set that your recipient will genuinely use and appreciate.</p>

<h3>Compatible Models</h3>
<p>We support the full range of iPhones, Samsung Galaxy series, OnePlus, Redmi, Realme, and more. Not sure if your model is available? Contact us and we'll check our current catalogue.</p>

<p>Stand out from the sea of identical devices with a case that's designed by you, for you. Order your custom mobile case from Manaprints and see how much personality fits in a pocket.</p>
    `,
    author: "Manaprints Team",
    publishedDate: "2026-05-15",
    category: "Mobile Cases",
    tags: ["mobile case", "custom printing", "personalisation", "smartphone accessories", "gifts"],
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=800&h=450&fit=crop",
    featured: false,
  },
  {
    id: 5,
    title: "Name Pencils: The Smartest Bulk Gift for Schools & Corporates",
    slug: "name-pencils-bulk-gift-schools-corporates",
    excerpt: "Name pencils are practical, affordable, and surprisingly memorable. Find out why schools, corporates, and event organisers across India are choosing custom printed pencils as their go-to bulk gift.",
    content: `
<p>In the world of promotional products and event giveaways, the humble pencil is often underestimated. But a personalised name pencil — printed with a student's name, a brand's logo, or an event's theme — becomes something entirely different: a keepsake that's used every day.</p>

<h3>Why Name Pencils Work</h3>
<p>Unlike branded merchandise that ends up in a drawer, pencils are used constantly. Every time a student picks up their name pencil, they feel a connection to the school or event that gave it to them. For brands, the daily touchpoint is invaluable — it's marketing with a functional product.</p>

<h3>School & Education Use Cases</h3>
<p><strong>Annual Day Gifts:</strong> Print each child's name on a set of pencils for a personal, affordable gift they'll treasure.</p>
<p><strong>Welcome Kits:</strong> Give new students personalised stationery to help them feel at home from day one.</p>
<p><strong>Achievement Rewards:</strong> Recognise top performers with personalised pencil sets in a branded pouch.</p>
<p><strong>Exam Season Motivation Packs:</strong> Add motivational quotes or the school's motto alongside the student's name.</p>

<h3>Corporate & Event Applications</h3>
<p><strong>Conferences & Seminars:</strong> Branded pencils with the event name and date are practical desk accessories attendees actually use.</p>
<p><strong>Client Gifting:</strong> Pair with a notepad and custom envelope for a complete stationery gift set.</p>
<p><strong>Employee Onboarding Kits:</strong> Welcome new hires with personalised stationery that includes their name from day one.</p>

<h3>Minimum Order & Pricing</h3>
<p>Manaprints' name pencils are available for bulk orders at competitive per-unit pricing that reduces with volume. We can personalise each pencil with a different name — no minimum quantity required per name variant. Perfect for school batches of any size.</p>

<h3>Customisation Options</h3>
<p>Choose from HB, 2B, or coloured pencil sets. Print options include single-colour foil stamping or full-colour digital printing. Boxes and pouches can also be printed with matching branding.</p>

<p>Simple, practical, personal. Name pencils from Manaprints are the bulk gift that leaves a lasting impression — one line at a time.</p>
    `,
    author: "Manaprints Team",
    publishedDate: "2026-05-02",
    category: "Name Pencils",
    tags: ["name pencils", "bulk gifts", "corporate gifts", "school supplies", "stationery"],
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=800&h=450&fit=crop",
    featured: false,
  },
  {
    id: 6,
    title: "Resin Printing: The Luxury Feel of Custom Home Accessories",
    slug: "resin-printing-luxury-custom-home-accessories",
    excerpt: "Resin products have exploded in popularity for their glass-like depth and premium appearance. Discover how Manaprints uses resin printing to create custom home accessories that feel genuinely luxurious.",
    content: `
<p>There's a certain magic about resin-printed products — the way light plays through them, the depth of the image, the smooth glass-like surface. At Manaprints, we harness this effect to produce custom resin accessories that look expensive, feel premium, and carry deeply personal meaning.</p>

<h3>What Is Resin Printing?</h3>
<p>Resin printing involves embedding a printed image or design within a clear resin coating. The result is a surface that looks three-dimensional and has extraordinary clarity — colours appear more vibrant, details more crisp, and the overall finish more elegant than standard printing methods.</p>

<h3>Popular Resin Products at Manaprints</h3>
<p><strong>Photo Tiles:</strong> Square or rectangular resin-coated photo prints that mount directly to walls without frames. Perfect for modern, gallery-style displays.</p>
<p><strong>Coasters:</strong> Functional and beautiful, personalised resin coasters with photos or patterns make a daily-use gift with serious wow factor.</p>
<p><strong>Keychains:</strong> Compact, durable, and deeply personal — resin photo keychains are one of our most gifted products.</p>
<p><strong>Desk Accessories:</strong> Custom resin paperweights, pen holders, and desk plaques bring personality to work spaces.</p>
<p><strong>Trophies & Awards:</strong> Resin plaques with etched or printed text are an elevated alternative to traditional acrylic awards.</p>

<h3>Why Choose Resin Over Standard Printing</h3>
<p>The resin layer acts as a protective coating, making the product water-resistant, scratch-resistant, and UV-stable. Unlike paper prints that fade or warp, resin products maintain their appearance for years under normal use.</p>

<h3>Gifting With Resin Products</h3>
<p>Resin products punch above their weight in perceived value. A resin photo coaster set costs far less than an equivalent piece of art, yet looks and feels luxurious. For birthdays, weddings, housewarmings, or festival gifting, resin accessories from Manaprints consistently impress recipients.</p>

<h3>Corporate Use</h3>
<p>Custom resin desk accessories and award plaques are ideal for corporate environments. They convey quality and attention to detail — values that reflect positively on the brand presenting them.</p>

<p>Explore our full range of resin products at Manaprints. From a single keepsake to bulk order corporate gifts, we deliver the luxury of resin at prices that make sense.</p>
    `,
    author: "Manaprints Team",
    publishedDate: "2026-04-20",
    category: "Resin",
    tags: ["resin", "home accessories", "custom printing", "luxury gifts", "photo gifts"],
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=450&fit=crop",
    featured: true,
  },
  {
    id: 7,
    title: "The Complete Guide to Custom Printing in India",
    slug: "complete-guide-custom-printing-india",
    excerpt: "From choosing the right product to uploading the perfect file, this guide walks you through everything you need to know about ordering custom prints in India — without the confusion.",
    content: `
<p>Custom printing in India has never been more accessible. With online platforms like Manaprints, you can design and order personalised products from anywhere in the country and have them delivered to your door. But if you're new to the world of custom printing, it can feel overwhelming. This guide breaks it down clearly.</p>

<h3>Step 1: Choose the Right Product</h3>
<p>Start with the end use in mind. Are you printing for gifting, home décor, personal use, or corporate promotion? Each scenario has a best-fit product. Photo albums and frames for gifts, pillow covers and resin accessories for home décor, mobile cases and stationery for personal use, and bulk items like name pencils for corporate purposes.</p>

<h3>Step 2: Prepare Your Image or Design</h3>
<p>Image quality is the most common cause of printing disappointment. Always use high-resolution files (minimum 300 DPI at the final print size). For photos, use original device photos rather than screenshots or downloads. For design files, export as PNG or PDF for best results.</p>

<h3>Step 3: Understand Colour Modes</h3>
<p>Screens display in RGB colour, while printers use CMYK. Colours can shift slightly during this conversion. At Manaprints, we use colour-accurate printing with soft proofing to minimise surprises, but it's good practice to test with a single item before placing large bulk orders.</p>

<h3>Step 4: Review Before Ordering</h3>
<p>Always use the online preview tool before confirming your order. Check that text is readable, images aren't too close to edges, and the overall composition looks balanced at the intended print size.</p>

<h3>Step 5: Choose the Right Finish</h3>
<p><strong>Matte:</strong> Elegant, glare-free, great for text-heavy designs and professional contexts.</p>
<p><strong>Glossy:</strong> Vibrant colours, high contrast, perfect for photography and bold graphics.</p>
<p><strong>Satin:</strong> A balance between matte and gloss — subtle sheen without harsh reflections.</p>

<h3>Bulk Orders: What to Know</h3>
<p>For orders of 50+ units, Manaprints offers volume pricing. Consistency across units is guaranteed by our standardised production process. Contact us for a custom quote on large batches.</p>

<h3>Delivery & Returns</h3>
<p>We ship across India with tracked delivery. Estimated times range from 3–7 business days depending on your location and order complexity. If your product arrives damaged or doesn't match the preview, we'll replace it — no questions asked.</p>

<p>Custom printing is simpler than it looks once you know the basics. Manaprints is here to guide you at every step, from first upload to final delivery.</p>
    `,
    author: "Manaprints Team",
    publishedDate: "2026-04-05",
    category: "Printing Guide",
    tags: ["custom printing", "guide", "India", "how-to", "printing tips"],
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1459257831348-f0cdd359235f?w=800&h=450&fit=crop",
    featured: false,
  },
  {
    id: 8,
    title: "Top 10 Custom Print Gifts for Every Occasion in 2026",
    slug: "top-custom-print-gifts-every-occasion-2026",
    excerpt: "Stuck on what to gift? We've rounded up the 10 best personalised print products from Manaprints that work for birthdays, weddings, festivals, graduations, and everything in between.",
    content: `
<p>The best gift is one that shows you were paying attention. Custom printed gifts do exactly that — they carry the recipient's name, face, or memory, making them impossible to give back and hard to forget. Here are the top 10 picks from Manaprints for every occasion in 2026.</p>

<h3>1. Custom Photo Album — For Milestones & Weddings</h3>
<p>Nothing beats a curated photo album for preserving the highlight reel of someone's life. Wedding gifts, anniversary presents, or graduation keepsakes — a printed album is the kind of gift people show their grandchildren.</p>

<h3>2. Personalised Pillow Cover — For Housewarmings</h3>
<p>A custom pillow with a family photo, pet portrait, or meaningful quote instantly personalises a new home. Practical, beautiful, and genuinely thoughtful.</p>

<h3>3. Custom Photo Frame — For Parents & Grandparents</h3>
<p>Grandparents especially treasure framed photos of their grandchildren. A quality frame with a quality print creates a gift that will stay on their mantlepiece for decades.</p>

<h3>4. Custom Mobile Case — For Teenagers & Young Adults</h3>
<p>Teenagers love a case that's uniquely theirs. Print their favourite photo, band logo, or personal artwork on a high-quality case they'll actually use every day.</p>

<h3>5. Resin Photo Coasters — For Colleagues & Neighbours</h3>
<p>The perfect "not too personal, not too generic" gift. A set of resin coasters with a family photo or scenic print is functional, premium-looking, and genuinely appreciated.</p>

<h3>6. Name Pencil Set — For Kids & Students</h3>
<p>For children's birthdays or school events, a personalised pencil set is a delight. Practical for school and exciting to receive because it has their name on it.</p>

<h3>7. Custom Keychain — For Best Friends</h3>
<p>A resin photo keychain with a memorable photo — first trip together, group photo, shared joke — is small, affordable, and carries enormous sentimental value.</p>

<h3>8. Custom Desk Plaque — For Corporate Recognition</h3>
<p>Employees who are recognised by name feel valued. A custom desk plaque with their name, title, and a message of appreciation costs little but means a great deal.</p>

<h3>9. Photo Tile Wall Set — For Interior Enthusiasts</h3>
<p>A set of resin photo tiles creates a stunning gallery wall. Curate a set around a theme — family, travel, or a single colour palette — for an instantly impressive display.</p>

<h3>10. Custom Bulk Gift Set — For Events & Corporate Gifting</h3>
<p>For Diwali, Christmas, or corporate events, a curated set combining a custom pillow cover, frame print, and name stationery creates a multi-piece gift that feels luxurious and is surprisingly affordable at scale.</p>

<p>Whatever the occasion, Manaprints has a personalised option that turns a thoughtful idea into a physical keepsake. Browse our full catalogue and start creating something unforgettable today.</p>
    `,
    author: "Manaprints Team",
    publishedDate: "2026-03-22",
    category: "Gift Guide",
    tags: ["gifts", "custom printing", "occasions", "personalised gifts", "ideas"],
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=800&h=450&fit=crop",
    featured: false,
  },
];

export const BlogProvider = ({ children }) => {
  const [searchTerm, setSearchTerm]           = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...Array.from(new Set(blogPosts.map(p => p.category)))];

  const featuredPosts = blogPosts.filter(p => p.featured);

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = !searchTerm ||
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getPostBySlug = (slug) => blogPosts.find(p => p.slug === slug) || null;

  const getRelatedPosts = (post, count = 3) =>
    blogPosts
      .filter(p => p.id !== post.id && (p.category === post.category || p.tags.some(t => post.tags.includes(t))))
      .slice(0, count);

  return (
    <BlogContext.Provider value={{
      posts: blogPosts,
      filteredPosts,
      featuredPosts,
      categories,
      searchTerm,
      setSearchTerm,
      selectedCategory,
      setSelectedCategory,
      getPostBySlug,
      getRelatedPosts,
    }}>
      {children}
    </BlogContext.Provider>
  );
};

export default BlogContext;
